import type { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { Button, ButtonVariants } from '@components/button/Button';
import { Divider } from '@components/divider/Divider';
import { Modal } from '@components/modal/Modal';
import type { EventFormData } from '../RestaurantEventForm';
import styles from './AssignTablesModal.scss';
import { AddOrRemoveTables } from './AddOrRemoveTables';
import { difference, union } from 'lodash-es';

interface AssignTablesModalProps {
  isOpen: boolean;
  tierIndex: number;
  onClose: () => void;
  onDone: () => void;
  watch: UseFormWatch<EventFormData>;
  setValue: UseFormSetValue<EventFormData>;
}

export const AssignTablesModal = ({
  isOpen,
  tierIndex,
  onClose,
  onDone,
  watch,
  setValue,
}: AssignTablesModalProps) => {
  const tierName = watch(`ticketTiers.${tierIndex}.name`) ?? '';
  const selectedFloorPlanTableIds =
    watch(`ticketTiers.${tierIndex}.floorPlanTableIds`) ?? [];

  const toggleFloorPlanTableId = (id: string) => {
    const floorPlanTableIds =
      watch(`ticketTiers.${tierIndex}.floorPlanTableIds`) ?? [];
    if (!floorPlanTableIds.includes(id)) {
      const next = [...floorPlanTableIds, id];
      setValue(`ticketTiers.${tierIndex}.floorPlanTableIds`, next);
      setValue(`ticketTiers.${tierIndex}.tableCount`, String(next.length));
    } else {
      const next = floorPlanTableIds.filter((tableId) => tableId !== id);
      setValue(`ticketTiers.${tierIndex}.floorPlanTableIds`, next);
      setValue(`ticketTiers.${tierIndex}.tableCount`, String(next.length));
    }
  };

  const handleSelectAll = (tableIdsForSelectedFloorPlan: string[]) => {
    const floorPlanTableIds =
      watch(`ticketTiers.${tierIndex}.floorPlanTableIds`) ?? [];
    const next = union(floorPlanTableIds, tableIdsForSelectedFloorPlan);
    setValue(`ticketTiers.${tierIndex}.floorPlanTableIds`, next);
    setValue(`ticketTiers.${tierIndex}.tableCount`, String(next.length));
  };

  const handleClearSelection = (tableIdsForSelectedFloorPlan: string[]) => {
    const floorPlanTableIds =
      watch(`ticketTiers.${tierIndex}.floorPlanTableIds`) ?? [];
    const next = difference(floorPlanTableIds, tableIdsForSelectedFloorPlan);
    setValue(`ticketTiers.${tierIndex}.floorPlanTableIds`, next);
    setValue(`ticketTiers.${tierIndex}.tableCount`, String(next.length));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Assign Tables"
      subtitle={`Select tables for the '${tierName}' tier`}
      titleClassName={styles.modalTitle}
      className={styles.modalContent}
    >
      <AddOrRemoveTables
        selectedFloorPlanTableIds={selectedFloorPlanTableIds}
        toggleFloorPlanTableId={toggleFloorPlanTableId}
        onSelectAll={handleSelectAll}
        onClearSelection={handleClearSelection}
      />
      <Divider className={styles.modalDivider} />
      <div className={styles.modalFooter}>
        <Button
          label="Done"
          onClick={onDone}
          variant={ButtonVariants.Secondary}
        />
      </div>
    </Modal>
  );
};
